import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Avatar,
  Chip,
  InputAdornment,
  Alert,
  LinearProgress,
  Pagination,
  useMediaQuery,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Add,
  Edit,
  Delete,
  Search,
  Inventory2,
  Warning,
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  LocalShipping,
  Archive,
  ExpandMore,
} from '@mui/icons-material';

// Styled Components con diseño El Piñón
const DashboardContainer = styled(Box)({
  background: 'linear-gradient(135deg, #f8fffe 0%, #e8f5e8 100%)',
  minHeight: '100vh',
  padding: '24px',
});

const HeaderSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '32px',
  padding: '24px',
  background: 'linear-gradient(135deg, #2e5a33 0%, #3d7342 100%)',
  borderRadius: '20px',
  color: 'white',
  boxShadow: '0 8px 32px rgba(46, 90, 51, 0.3)',
});

const StatsCard = styled(Card)({
  background: 'linear-gradient(135deg, #fff 0%, #fdfaf4 100%)',
  borderRadius: '16px',
  border: '2px solid #e8f5e8',
  boxShadow: '0 4px 20px rgba(46, 90, 51, 0.1)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 30px rgba(46, 90, 51, 0.2)',
    borderColor: '#2e5a33',
  },
});

const ActionButton = styled(Button)({
  background: 'linear-gradient(135deg, #2e5a33 0%, #3d7342 100%)',
  color: 'white',
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '12px 24px',
  boxShadow: '0 4px 15px rgba(46, 90, 51, 0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, #3d7342 0%, #2e5a33 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(46, 90, 51, 0.4)',
  },
});

// Styled Accordion para móvil
const StyledAccordion = styled(Accordion)({
  background: 'linear-gradient(135deg, #fff 0%, #fdfaf4 100%)',
  border: '1px solid #e8f5e8',
  borderRadius: '12px !important',
  marginBottom: '12px',
  boxShadow: '0 2px 10px rgba(46, 90, 51, 0.08)',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: '0 0 12px 0',
    borderColor: '#2e5a33',
    boxShadow: '0 4px 20px rgba(46, 90, 51, 0.15)',
  },
});

const AccordionHeader = styled(AccordionSummary)({
  minHeight: '72px',
  padding: '8px 16px',
  '&.Mui-expanded': {
    minHeight: '72px',
    backgroundColor: 'rgba(46, 90, 51, 0.03)',
  },
  '& .MuiAccordionSummary-content': {
    margin: '12px 0',
    '&.Mui-expanded': {
      margin: '12px 0',
    },
  },
});

const AccordionBody = styled(AccordionDetails)({
  padding: '0 16px 16px 16px',
  backgroundColor: 'rgba(248, 255, 254, 0.5)',
});

const DetailRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: '10px 0',
  borderBottom: '1px solid rgba(46, 90, 51, 0.08)',
  '&:last-child': {
    borderBottom: 'none',
  },
});

// Interfaces
interface ProductoInventario {
  id: string;
  codigo: string;
  nombre: string;
  categoria: string;
  stockActual: number;
  stockMinimo: number;
  stockMaximo: number;
  ubicacion: string;
  lote: string;
  fechaVencimiento: string;
  proveedor: string;
  costoUnitario: number;
  valorInventario: number;
  ultimoMovimiento: string;
}

interface MovimientoInventario {
  id: string;
  productoId: string;
  tipo: 'entrada' | 'salida' | 'ajuste';
  cantidad: number;
  motivo: string;
  fecha: string;
  usuario: string;
}

interface StatCardProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, subtitle }) => (
  <StatsCard>
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="h4" fontWeight={700} color={color} gutterBottom>
            {value}
          </Typography>
          <Typography variant="h6" color="#2e5a33" fontWeight={600}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Avatar
          sx={{
            backgroundColor: `${color}15`,
            color: color,
            width: 64,
            height: 64,
          }}
        >
          {icon}
        </Avatar>
      </Box>
    </CardContent>
  </StatsCard>
);

const ITEMS_PER_PAGE = 8;

const DashboardInventario: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [productos, setProductos] = useState<ProductoInventario[]>([]);
  const [filteredProductos, setFilteredProductos] = useState<ProductoInventario[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [dialogMovimiento, setDialogMovimiento] = useState(false);
  const [selectedProducto, setSelectedProducto] = useState<ProductoInventario | null>(null);
  const [movimientoData, setMovimientoData] = useState({
    tipo: 'entrada' as 'entrada' | 'salida' | 'ajuste',
    cantidad: 0,
    motivo: '',
  });

  // Mock data
  useEffect(() => {
    const mockProductos: ProductoInventario[] = [
      {
        id: '1',
        codigo: 'PIN001',
        nombre: 'Piñones Premium 500g',
        categoria: 'Frutos Secos',
        stockActual: 45,
        stockMinimo: 10,
        stockMaximo: 100,
        ubicacion: 'Estante A-1',
        lote: 'LT-2024-001',
        fechaVencimiento: '2025-06-15',
        proveedor: 'Cooperativa Araucaria',
        costoUnitario: 18000,
        valorInventario: 810000,
        ultimoMovimiento: '2024-10-30',
      },
      {
        id: '2',
        codigo: 'MIE002',
        nombre: 'Miel de Araucaria 250ml',
        categoria: 'Mieles',
        stockActual: 8,
        stockMinimo: 15,
        stockMaximo: 50,
        ubicacion: 'Estante B-2',
        lote: 'LT-2024-002',
        fechaVencimiento: '2026-12-31',
        proveedor: 'Apiarios del Sur',
        costoUnitario: 8500,
        valorInventario: 68000,
        ultimoMovimiento: '2024-10-28',
      },
      {
        id: '3',
        codigo: 'ACE003',
        nombre: 'Aceite de Araucaria 500ml',
        categoria: 'Aceites',
        stockActual: 22,
        stockMinimo: 5,
        stockMaximo: 30,
        ubicacion: 'Estante C-1',
        lote: 'LT-2024-003',
        fechaVencimiento: '2025-08-20',
        proveedor: 'Aceites Orgánicos Chile',
        costoUnitario: 25000,
        valorInventario: 550000,
        ultimoMovimiento: '2024-10-29',
      },
      {
        id: '4',
        codigo: 'FRU004',
        nombre: 'Mix Frutos Secos 1kg',
        categoria: 'Frutos Secos',
        stockActual: 2,
        stockMinimo: 10,
        stockMaximo: 40,
        ubicacion: 'Estante A-3',
        lote: 'LT-2024-004',
        fechaVencimiento: '2025-03-15',
        proveedor: 'Frutos Secos Andinos',
        costoUnitario: 12000,
        valorInventario: 24000,
        ultimoMovimiento: '2024-10-25',
      },
    ];
    
    setProductos(mockProductos);
    setFilteredProductos(mockProductos);
  }, []);

  // Search filter
  useEffect(() => {
    const filtered = productos.filter(producto =>
      producto.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      producto.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      producto.categoria.toLowerCase().includes(searchTerm.toLowerCase()) ||
      producto.proveedor.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredProductos(filtered);
    setCurrentPage(1); // Reset page when search changes
  }, [searchTerm, productos]);

  // Pagination
  const totalPages = Math.ceil(filteredProductos.length / ITEMS_PER_PAGE);
  const paginatedProductos = filteredProductos.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getStockStatus = (producto: ProductoInventario) => {
    if (producto.stockActual <= producto.stockMinimo) {
      return { status: 'critico', color: '#d32f2f', label: 'Stock Crítico' };
    } else if (producto.stockActual <= producto.stockMinimo * 1.5) {
      return { status: 'bajo', color: '#ff9800', label: 'Stock Bajo' };
    } else if (producto.stockActual >= producto.stockMaximo * 0.9) {
      return { status: 'alto', color: '#2196f3', label: 'Stock Alto' };
    } else {
      return { status: 'normal', color: '#4caf50', label: 'Stock Normal' };
    }
  };

  const getStockPercentage = (producto: ProductoInventario) => {
    return (producto.stockActual / producto.stockMaximo) * 100;
  };

  const handleMovimiento = (producto: ProductoInventario) => {
    setSelectedProducto(producto);
    setMovimientoData({
      tipo: 'entrada',
      cantidad: 0,
      motivo: '',
    });
    setDialogMovimiento(true);
  };

  const confirmarMovimiento = () => {
    if (!selectedProducto || movimientoData.cantidad <= 0) return;

    const nuevoStock = selectedProducto.stockActual + 
      (movimientoData.tipo === 'salida' ? -movimientoData.cantidad : movimientoData.cantidad);

    setProductos(productos.map(prod => 
      prod.id === selectedProducto.id 
        ? { 
            ...prod, 
            stockActual: nuevoStock,
            valorInventario: nuevoStock * prod.costoUnitario,
            ultimoMovimiento: new Date().toISOString().split('T')[0]
          }
        : prod
    ));

    setDialogMovimiento(false);
    setSelectedProducto(null);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
    }).format(amount);
  };

  const stats = {
    totalProductos: productos.length,
    stockCritico: productos.filter(p => p.stockActual <= p.stockMinimo).length,
    valorTotal: productos.reduce((sum, p) => sum + p.valorInventario, 0),
    movimientosHoy: 12, // Mock data
  };

  const productosStockCritico = productos.filter(p => p.stockActual <= p.stockMinimo);

  return (
    <DashboardContainer>
      {/* Header */}
      <HeaderSection>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar
            src="/assets/logos/logo-el-pinon-nuevo.png"
            sx={{ width: 60, height: 60, border: '3px solid rgba(255,255,255,0.3)' }}
          />
          <Box>
            <Typography variant="h4" fontWeight={700} fontFamily="'Playfair Display', serif">
              Control de Inventario - El Piñón
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Gestiona el stock y movimientos de inventario
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={2}>
          <ActionButton onClick={() => {}}>
            <Archive sx={{ mr: 1 }} />
            Inventario Físico
          </ActionButton>
        </Box>
      </HeaderSection>

      {/* Alertas de Stock Crítico */}
      {productosStockCritico.length > 0 && (
        <Alert 
          severity="error" 
          sx={{ mb: 3, borderRadius: '12px' }}
          icon={<Warning />}
        >
          <Typography fontWeight={600}>
            ¡Atención! {productosStockCritico.length} producto(s) con stock crítico:
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {productosStockCritico.map(p => p.nombre).join(', ')}
          </Typography>
        </Alert>
      )}

      {/* Stats Cards */}
      <Box display="flex" gap={3} marginBottom={4} flexWrap="wrap">
        <Box flex="1 1 250px">
          <StatCard
            title="Total Productos"
            value={stats.totalProductos.toString()}
            icon={<Inventory2 />}
            color="#2e5a33"
            subtitle="En inventario"
          />
        </Box>
        <Box flex="1 1 250px">
          <StatCard
            title="Stock Crítico"
            value={stats.stockCritico.toString()}
            icon={<Warning />}
            color="#d32f2f"
            subtitle="Requieren reposición"
          />
        </Box>
        <Box flex="1 1 250px">
          <StatCard
            title="Valor Total Inventario"
            value={formatCurrency(stats.valorTotal)}
            icon={<TrendingUp />}
            color="#9c27b0"
            subtitle="Valor del stock"
          />
        </Box>
        <Box flex="1 1 250px">
          <StatCard
            title="Movimientos Hoy"
            value={stats.movimientosHoy.toString()}
            icon={<LocalShipping />}
            color="#2196f3"
            subtitle="Entradas y salidas"
          />
        </Box>
      </Box>

      {/* Search and Table */}
      <Paper sx={{ p: 3, borderRadius: '16px', boxShadow: '0 4px 20px rgba(46, 90, 51, 0.1)' }}>
        <Box mb={3}>
          <TextField
            fullWidth
            placeholder="Buscar productos en inventario..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                '&:hover fieldset': {
                  borderColor: '#2e5a33',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#2e5a33',
                },
              },
            }}
          />
        </Box>

        {/* Vista Móvil - Acordeón */}
        {isMobile ? (
          <Box>
            {paginatedProductos.length === 0 ? (
              <Box display="flex" justifyContent="center" py={4}>
                <Typography color="text.secondary">No hay productos en inventario</Typography>
              </Box>
            ) : (
              paginatedProductos.map((producto) => {
                const stockStatus = getStockStatus(producto);
                const stockPercentage = getStockPercentage(producto);
                
                return (
                  <StyledAccordion
                    key={producto.id}
                    expanded={expandedId === producto.id}
                    onChange={(_, isExpanded) => setExpandedId(isExpanded ? producto.id : null)}
                  >
                    <AccordionHeader expandIcon={<ExpandMore sx={{ color: '#2e5a33' }} />}>
                      <Box display="flex" alignItems="center" gap={2} flex={1}>
                        <Avatar sx={{ backgroundColor: '#2e5a33', color: 'white', width: 40, height: 40 }}>
                          <Inventory2 fontSize="small" />
                        </Avatar>
                        <Box flex={1}>
                          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Typography fontWeight={600} fontSize="0.95rem">{producto.nombre}</Typography>
                            <Chip
                              label={stockStatus.label}
                              size="small"
                              sx={{
                                backgroundColor: `${stockStatus.color}15`,
                                color: stockStatus.color,
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                height: '22px',
                              }}
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                            {producto.codigo} • Stock: {producto.stockActual}
                          </Typography>
                        </Box>
                      </Box>
                    </AccordionHeader>
                    <AccordionBody>
                      <Divider sx={{ mb: 2, borderColor: 'rgba(46, 90, 51, 0.15)' }} />
                      
                      {/* Stock con barra de progreso */}
                      <DetailRow>
                        <Typography color="text.secondary" fontSize="0.85rem" fontWeight={500}>Stock</Typography>
                        <Box textAlign="right" width="60%">
                          <Typography variant="h6" fontWeight={600}>{producto.stockActual}</Typography>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(stockPercentage, 100)}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              backgroundColor: '#f5f5f5',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: stockStatus.color,
                                borderRadius: 3,
                              },
                            }}
                          />
                          <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                            Min: {producto.stockMinimo} • Max: {producto.stockMaximo}
                          </Typography>
                        </Box>
                      </DetailRow>

                      <DetailRow>
                        <Typography color="text.secondary" fontSize="0.85rem" fontWeight={500}>Categoría</Typography>
                        <Typography textAlign="right">{producto.categoria}</Typography>
                      </DetailRow>

                      <DetailRow>
                        <Typography color="text.secondary" fontSize="0.85rem" fontWeight={500}>Ubicación</Typography>
                        <Typography textAlign="right">{producto.ubicacion}</Typography>
                      </DetailRow>

                      <DetailRow>
                        <Typography color="text.secondary" fontSize="0.85rem" fontWeight={500}>Lote</Typography>
                        <Box textAlign="right">
                          <Typography>{producto.lote}</Typography>
                          <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                            Vence: {new Date(producto.fechaVencimiento).toLocaleDateString('es-CL')}
                          </Typography>
                        </Box>
                      </DetailRow>

                      <DetailRow>
                        <Typography color="text.secondary" fontSize="0.85rem" fontWeight={500}>Proveedor</Typography>
                        <Typography textAlign="right">{producto.proveedor}</Typography>
                      </DetailRow>

                      <DetailRow>
                        <Typography color="text.secondary" fontSize="0.85rem" fontWeight={500}>Valor</Typography>
                        <Box textAlign="right">
                          <Typography fontWeight={600} color="#2e5a33">
                            {formatCurrency(producto.valorInventario)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                            {formatCurrency(producto.costoUnitario)}/unidad
                          </Typography>
                        </Box>
                      </DetailRow>

                      {/* Acciones */}
                      <Divider sx={{ my: 2, borderColor: 'rgba(46, 90, 51, 0.15)' }} />
                      <Box display="flex" justifyContent="center" gap={2}>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<TrendingUp />}
                          onClick={() => handleMovimiento(producto)}
                          sx={{
                            backgroundColor: '#2e5a33',
                            '&:hover': { backgroundColor: '#3d7342' },
                          }}
                        >
                          Movimiento
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Archive />}
                          sx={{
                            borderColor: '#2196f3',
                            color: '#2196f3',
                            '&:hover': { borderColor: '#1976d2', backgroundColor: 'rgba(33, 150, 243, 0.08)' },
                          }}
                        >
                          Historial
                        </Button>
                      </Box>
                    </AccordionBody>
                  </StyledAccordion>
                );
              })
            )}
          </Box>
        ) : (
          /* Vista Desktop - Tabla */
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: 900 }}>
              <Box 
                display="grid" 
                gridTemplateColumns="2fr 1.2fr 1fr 1fr 1.2fr 1.2fr 0.8fr"
                gap={2}
                p={2}
                sx={{ 
                  backgroundColor: 'rgba(46, 90, 51, 0.03)',
                  borderRadius: '12px 12px 0 0',
                  borderBottom: '2px solid #e8f5e8',
                }}
              >
                <Typography fontWeight={600} color="#2e5a33">Producto</Typography>
                <Typography fontWeight={600} color="#2e5a33">Stock</Typography>
                <Typography fontWeight={600} color="#2e5a33">Estado</Typography>
                <Typography fontWeight={600} color="#2e5a33">Ubicación</Typography>
                <Typography fontWeight={600} color="#2e5a33">Lote/Vencimiento</Typography>
                <Typography fontWeight={600} color="#2e5a33">Valor</Typography>
                <Typography fontWeight={600} color="#2e5a33">Acciones</Typography>
              </Box>
              
              {paginatedProductos.map((producto) => {
                const stockStatus = getStockStatus(producto);
                const stockPercentage = getStockPercentage(producto);
                
                return (
                  <Box 
                    key={producto.id}
                    display="grid" 
                    gridTemplateColumns="2fr 1.2fr 1fr 1fr 1.2fr 1.2fr 0.8fr"
                    gap={2}
                    p={2}
                    alignItems="center"
                    sx={{ 
                      borderBottom: '1px solid #e8f5e8',
                      '&:hover': { backgroundColor: 'rgba(46, 90, 51, 0.02)' },
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar sx={{ backgroundColor: '#2e5a33', color: 'white' }}>
                        <Inventory2 />
                      </Avatar>
                      <Box>
                        <Typography fontWeight={600}>{producto.nombre}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {producto.codigo} • {producto.categoria}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {producto.proveedor}
                        </Typography>
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight={600}>
                        {producto.stockActual}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(stockPercentage, 100)}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          backgroundColor: '#f5f5f5',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: stockStatus.color,
                            borderRadius: 3,
                          },
                        }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        Min: {producto.stockMinimo} • Max: {producto.stockMaximo}
                      </Typography>
                    </Box>
                    <Box>
                      <Chip
                        label={stockStatus.label}
                        size="small"
                        sx={{
                          backgroundColor: `${stockStatus.color}15`,
                          color: stockStatus.color,
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                    <Typography variant="body2">{producto.ubicacion}</Typography>
                    <Box>
                      <Typography variant="body2">{producto.lote}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Vence: {new Date(producto.fechaVencimiento).toLocaleDateString('es-CL')}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography fontWeight={600} color="#2e5a33">
                        {formatCurrency(producto.valorInventario)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatCurrency(producto.costoUnitario)}/unidad
                      </Typography>
                    </Box>
                    <Box display="flex" gap={1}>
                      <IconButton
                        size="small"
                        onClick={() => handleMovimiento(producto)}
                        sx={{ color: '#2e5a33' }}
                        title="Registrar movimiento"
                      >
                        <TrendingUp />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {}}
                        sx={{ color: '#2196f3' }}
                        title="Ver historial"
                      >
                        <Archive />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Paginación */}
        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" alignItems="center" mt={3} gap={2}>
            <Typography variant="body2" color="text.secondary">
              Mostrando {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, filteredProductos.length)} de {filteredProductos.length} productos
            </Typography>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  color: '#2e5a33',
                  '&.Mui-selected': {
                    backgroundColor: '#2e5a33',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#3d7342',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(46, 90, 51, 0.1)',
                  },
                },
              }}
            />
          </Box>
        )}
      </Paper>

      {/* Dialog Movimiento */}
      <Dialog open={dialogMovimiento} onClose={() => setDialogMovimiento(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ backgroundColor: '#2e5a33', color: 'white' }}>
              <LocalShipping />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Registrar Movimiento
              </Typography>
              {selectedProducto && (
                <Typography variant="body2" color="text.secondary">
                  {selectedProducto.nombre} (Stock actual: {selectedProducto.stockActual})
                </Typography>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 2 }}>
            <Box display="flex" gap={2}>
              {(['entrada', 'salida', 'ajuste'] as const).map((tipo) => (
                <Button
                  key={tipo}
                  variant={movimientoData.tipo === tipo ? 'contained' : 'outlined'}
                  onClick={() => setMovimientoData({ ...movimientoData, tipo })}
                  sx={{
                    backgroundColor: movimientoData.tipo === tipo ? '#2e5a33' : 'transparent',
                    borderColor: '#2e5a33',
                    color: movimientoData.tipo === tipo ? 'white' : '#2e5a33',
                    '&:hover': {
                      backgroundColor: movimientoData.tipo === tipo ? '#3d7342' : 'rgba(46, 90, 51, 0.1)',
                    },
                  }}
                >
                  {tipo === 'entrada' && <TrendingUp sx={{ mr: 1 }} />}
                  {tipo === 'salida' && <TrendingDown sx={{ mr: 1 }} />}
                  {tipo === 'ajuste' && <Edit sx={{ mr: 1 }} />}
                  {tipo.charAt(0).toUpperCase() + tipo.slice(1)}
                </Button>
              ))}
            </Box>
            <TextField
              label="Cantidad"
              type="number"
              fullWidth
              value={movimientoData.cantidad}
              onChange={(e) => setMovimientoData({ ...movimientoData, cantidad: parseInt(e.target.value) || 0 })}
              required
            />
            <TextField
              label="Motivo del movimiento"
              fullWidth
              multiline
              rows={2}
              value={movimientoData.motivo}
              onChange={(e) => setMovimientoData({ ...movimientoData, motivo: e.target.value })}
              placeholder="Ej: Venta, Compra, Ajuste por inventario, etc."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogMovimiento(false)}>Cancelar</Button>
          <ActionButton onClick={confirmarMovimiento}>
            Registrar Movimiento
          </ActionButton>
        </DialogActions>
      </Dialog>
    </DashboardContainer>
  );
};

export default DashboardInventario;